import { useCallback, useEffect, useState } from "react";

import { useRouter } from "next/router";

import { useFreshCurrentAccountResolution } from "@/hooks/use-current-account";
import { NEW_UI_FLAG_KEY } from "@/hooks/use-new-ui";
import { usePostHogFeatureFlag } from "@/hooks/use-posthog-feature-flag";
import { resolveLegacyRouteTransition, type LegacyRouteTransition } from "@/lib/legacy-route-transition";
import { isOfficialTestAccount } from "@/lib/test-account-environment";

export type LegacyRouteRedirectResult = {
  retry: () => void;
  transition: LegacyRouteTransition | "failed";
};

/**
 * Para páginas LEGADAS: manda a coorte que enxerga a UI nova (NMG nativo ou
 * tratamento do A/B) para a tela nova equivalente. Controle e flag desligada
 * não redirecionam — desligar a flag no PostHog reverte tudo na hora.
 *
 * Mapa canônico (plano W0.5):
 *   /quizzes, /missoes-old, /missoes/aprovar            → /atividades
 *   /players, /details, /attitudes                      → /classes
 *   /purchases, /requests, /rewards, /gift, /balance/*  → /rewards/redesign
 */
export function useLegacyRouteRedirect(target: string | null): LegacyRouteRedirectResult {
  const router = useRouter();
  const accountResolution = useFreshCurrentAccountResolution();
  const flag = usePostHogFeatureFlag(NEW_UI_FLAG_KEY);
  const account = accountResolution.account;
  const [failedTarget, setFailedTarget] = useState<string | null>(null);
  const [redirectAttempt, setRedirectAttempt] = useState(0);
  const isNewManagerGlobal = Boolean(account?.["new-manager-global"]);
  const isLoading = accountResolution.status !== "resolved" || (!isNewManagerGlobal && flag === undefined);
  const isNewUi = isNewManagerGlobal || flag === true;
  // Conta teste navega livremente por TODAS as páginas (novas e legadas):
  // nunca redireciona, independente do modo de UI simulado.
  const resolvedTransition = target
    ? resolveLegacyRouteTransition({
        isLoading,
        isNewUi,
        isRedirectExempt: isOfficialTestAccount(account),
      })
    : "render";
  const transition = accountResolution.status === "error" || (failedTarget === target && resolvedTransition === "redirecting")
    ? "failed"
    : resolvedTransition;

  useEffect(() => {
    // target null = página renderizada embutida (justComponents) — não redireciona.
    if (!target || resolvedTransition !== "redirecting") return;

    let active = true;
    void router.replace(target)
      .then(didNavigate => {
        if (active && didNavigate === false) setFailedTarget(target);
      })
      .catch(() => {
        if (active) setFailedTarget(target);
      });

    return () => {
      active = false;
    };
  }, [redirectAttempt, resolvedTransition, router, target]);

  const retry = useCallback(() => {
    if (accountResolution.status === "error") {
      accountResolution.retry();
      return;
    }

    setFailedTarget(null);
    setRedirectAttempt(attempt => attempt + 1);
  }, [accountResolution]);

  return { retry, transition };
}
